import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Search, BookOpen, DollarSign, GraduationCap, FileText, PiggyBank } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';

const faqData = [
  {
    category: "Understanding Costs",
    icon: DollarSign,
    color: "text-emerald-400",
    questions: [
      {
        q: "What's the difference between sticker price and net price?",
        a: "Sticker price (Cost of Attendance) is the full published cost before any aid. Net price is what you actually pay after scholarships, grants, and other aid are subtracted. For many students, especially at private schools, net price is significantly lower than sticker price."
      },
      {
        q: "What costs are included in Cost of Attendance (COA)?",
        a: "COA typically includes: tuition and fees, room and board (housing + meals), books and supplies, transportation, and personal expenses. Some schools also include health insurance. Our calculator breaks down each component so you can see exactly where your money goes."
      },
      {
        q: "Why do costs increase each year?",
        a: "College costs typically rise 3-5% annually due to inflation, rising operational costs, and increased services. Our calculator projects these increases over 4 years so you're not surprised by sophomore-year bills that are higher than freshman year."
      },
      {
        q: "What's the difference between in-state and out-of-state tuition?",
        a: "Public universities charge lower tuition to residents of their state (in-state) because their families pay state taxes that fund the university. Out-of-state students pay significantly more—sometimes 2-3x higher. Our calculator automatically applies in-state rates when you select your home state."
      }
    ]
  },
  {
    category: "Financial Aid",
    icon: FileText,
    color: "text-cyan-400",
    questions: [
      {
        q: "What is the EFC (Expected Family Contribution)?",
        a: "EFC (now called Student Aid Index or SAI) is the amount the government calculates your family can afford to pay for college annually. It's determined by your family's income, assets, household size, and number of students in college. Schools use this to determine your financial aid eligibility."
      },
      {
        q: "What's the difference between grants and loans?",
        a: "Grants are 'free money' that you don't have to repay. They're based on financial need (like Pell Grants) or merit (academic scholarships). Loans must be repaid with interest after graduation. Always maximize grants and scholarships before taking loans."
      },
      {
        q: "What is a Pell Grant and do I qualify?",
        a: "Pell Grants are federal grants for undergraduate students with financial need. The maximum award is $7,395 for 2024-25. You may qualify if your family income is below ~$60,000, though some students with higher incomes can receive partial grants. Complete the FAFSA to find out."
      },
      {
        q: "What's the difference between need-based and merit-based aid?",
        a: "Need-based aid is awarded based on your family's financial situation (demonstrated through FAFSA). Merit-based aid is awarded for achievements like high GPA, test scores, athletics, or special talents—regardless of financial need. Many students receive a combination of both."
      },
      {
        q: "When should I file the FAFSA?",
        a: "File the FAFSA as early as possible after October 1st of your senior year (or the year before you start college). Some aid is first-come, first-served, so early filers have an advantage. The FAFSA deadline varies by state and school—check both."
      }
    ]
  },
  {
    category: "Loans & Repayment",
    icon: PiggyBank,
    color: "text-amber-400",
    questions: [
      {
        q: "What's the difference between federal and private loans?",
        a: "Federal loans (Direct Subsidized/Unsubsidized, PLUS) offer fixed interest rates, flexible repayment options, and forgiveness programs. Private loans are from banks/credit unions with variable rates based on credit score, fewer protections, and no forgiveness options. Always max out federal loans first."
      },
      {
        q: "How much can I borrow in federal student loans?",
        a: "Dependent undergraduates can borrow: $5,500 (freshman), $6,500 (sophomore), $7,500 (junior/senior) annually, up to $31,000 total. Independent students have higher limits. Graduate students can borrow up to the full cost of attendance. Parent PLUS loans cover remaining costs."
      },
      {
        q: "What is income-driven repayment (IDR)?",
        a: "IDR plans cap your monthly federal loan payments at a percentage of your discretionary income (typically 10-20%). After 20-25 years of payments, remaining balances may be forgiven. Plans include SAVE, PAYE, IBR, and ICR. Great option if your income is low relative to your debt."
      },
      {
        q: "What is Public Service Loan Forgiveness (PSLF)?",
        a: "PSLF forgives remaining federal loan balances after 120 qualifying payments (10 years) while working full-time for a government or nonprofit employer. You must be on an IDR plan and make payments through the correct servicer. It's tax-free forgiveness."
      },
      {
        q: "Should I pay off loans early or invest?",
        a: "Generally, if your loan interest rate is above 6-7%, prioritize paying it down. Below that, investing may yield better returns over time. However, the guaranteed 'return' of paying off debt (no risk) vs. market uncertainty makes debt payoff appealing for risk-averse borrowers."
      }
    ]
  },
  {
    category: "Choosing a College",
    icon: GraduationCap,
    color: "text-purple-400",
    questions: [
      {
        q: "Is an expensive private school worth it?",
        a: "It depends on your net cost and career goals. Many private schools offer generous aid that makes them comparable to public school costs. Compare net prices, not sticker prices. For some careers (elite business, law), school prestige matters more. For others (nursing, teaching), choose the affordable option."
      },
      {
        q: "Should I start at community college to save money?",
        a: "Community college can save $20,000-$40,000 over two years. It works well if you: have a clear transfer plan, choose a school with articulation agreements, and maintain good grades. Downsides include less campus life and potential credit transfer issues. It's an excellent financial decision for many students."
      },
      {
        q: "How much debt is too much?",
        a: "A common rule: don't borrow more than your expected first-year salary. For a $50,000 starting salary, limit total debt to $50,000. Monthly payments should be under 10-15% of your take-home pay. Our calculator shows you exactly how your debt translates to monthly payments."
      },
      {
        q: "Does college ranking matter?",
        a: "Rankings matter most for: highly competitive fields (finance, consulting, big law), graduate school admissions, and networking opportunities. They matter less for: most careers, local job markets, and fields where skills/certifications matter more than pedigree. Don't go broke chasing prestige."
      }
    ]
  },
  {
    category: "Scholarships",
    icon: BookOpen,
    color: "text-pink-400",
    questions: [
      {
        q: "Where can I find scholarships?",
        a: "Start with: your intended college's financial aid office, local community foundations, your employer (or parents' employers), professional associations in your field, and scholarship search engines like Fastweb, Scholarships.com, and Bold.org. Also check state scholarship programs."
      },
      {
        q: "Are small scholarships worth the effort?",
        a: "Yes! Ten $1,000 scholarships = $10,000 toward your education. Smaller, local scholarships often have fewer applicants, giving you better odds. The time invested in applications (even for $500) often yields a better hourly 'wage' than any part-time job."
      },
      {
        q: "Do scholarships affect my financial aid?",
        a: "Outside scholarships first reduce your 'unmet need' gap. If you have no gap, they typically reduce loans first (good), then work-study, then grants (less good). Check with your school's policy. Some schools allow outside scholarships to reduce your EFC—ask your financial aid office."
      },
      {
        q: "Can I negotiate financial aid?",
        a: "Yes, this is called an 'appeal' or 'professional judgment review.' You can request more aid if: you have competing offers from similar schools, your family circumstances changed (job loss, medical bills), or your initial FAFSA didn't capture your full situation. Be polite, specific, and provide documentation."
      }
    ]
  }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-slate-800 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 text-left hover:text-emerald-400 transition-colors"
      >
        <span className="text-white font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 animate-in slide-in-from-top-2 duration-200">
          <p className="text-slate-300 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  
  // Filter questions by search
  const filteredFAQ = searchQuery
    ? faqData.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
               q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqData;
  
  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">Frequently Asked Questions</h1>
            <p className="text-slate-400 max-w-xl mx-auto">
              Everything you need to know about college costs, financial aid, and making smart decisions
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-900/50 border-slate-700 text-white pl-12 h-14 text-lg"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !activeCategory 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              All Topics
            </button>
            {faqData.map(category => (
              <button
                key={category.category}
                onClick={() => setActiveCategory(category.category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeCategory === category.category 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <category.icon className="w-4 h-4" />
                {category.category}
              </button>
            ))}
          </div>

          {/* FAQ Content */}
          <div className="space-y-8">
            {filteredFAQ
              .filter(category => !activeCategory || category.category === activeCategory)
              .map(category => (
                <Card key={category.category} className="bg-slate-900/50 border-slate-800 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center`}>
                      <category.icon className={`w-5 h-5 ${category.color}`} />
                    </div>
                    <h2 className="text-xl font-bold text-white">{category.category}</h2>
                  </div>
                  
                  <div className="space-y-0">
                    {category.questions.map((item, index) => (
                      <FAQItem key={index} question={item.q} answer={item.a} />
                    ))}
                  </div>
                </Card>
              ))}
          </div>

          {filteredFAQ.length === 0 && (
            <Card className="bg-slate-900/50 border-slate-800 p-12 text-center">
              <p className="text-slate-400 text-lg">No questions found matching your search.</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="text-emerald-400 hover:text-emerald-300 mt-2"
              >
                Clear search
              </button>
            </Card>
          )}

          {/* Still have questions */}
          <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/20 p-8 mt-8 text-center">
            <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
            <p className="text-slate-400 mb-4">
              We're here to help you make informed decisions about college affordability.
            </p>
            <a 
              href="/contact" 
              className="inline-block bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Contact Us
            </a>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
